import { useEffect, useState } from "react";
import type { PromptBindingVersion } from "@/types/llmTrace";
import type { PromptBinding } from "@/utils/trace/model";

/**
 * The prompt row a turn was built from, fetched when its badge is pressed.
 *
 * Editing a prompt only deactivates the old row, so the version a past answer
 * ran on is still readable — which is the whole point. A trace says "this
 * answer cost 22k tokens on base_prompt v14"; without this you can only see
 * whatever is active now, which may be several versions along and is exactly
 * the thing a prompt regression is being blamed on.
 *
 * Not carried in the trace itself: the tool guideline alone is ~27k characters,
 * and embedding every block of every turn would multiply the response for text
 * nobody has asked to see yet.
 */

/** What the backend returns, or why it could not. */
type Load =
  | { state: "loading" }
  | { state: "ready"; version: PromptBindingVersion }
  | { state: "error"; message: string };

export type LoadPromptVersion = (
  name: string,
  id: string,
) => Promise<PromptBindingVersion>;

// A fetched page can run to megabytes; nothing in the panel is worth that. Same
// ceiling TraceNodeDetail uses, so the two read alike.
const MAX_BODY_CHARS = 20000;

function clip(text: string) {
  return text.length > MAX_BODY_CHARS
    ? `${text.slice(0, MAX_BODY_CHARS)}\n… truncated`
    : text;
}

function Block({ title, children }: { title: string; children: string }) {
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">{title}</div>
      <pre className="block max-h-96 max-w-full overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-surface-input p-3 font-mono text-xs whitespace-pre-wrap text-code-foreground wrap-anywhere">
        {children}
      </pre>
    </div>
  );
}

export function PromptSourceView({
  binding,
  load,
  cache,
}: {
  binding: PromptBinding;
  load: LoadPromptVersion;
  /**
   * Shared across every row, keyed by prompt id. The same base_prompt version
   * backs every turn of an answer, so without this opening the badge on each
   * turn in a six-turn trace would fetch the same 27k characters six times.
   */
  cache: Map<string, PromptBindingVersion>;
}) {
  const cached = cache.get(binding.id);
  const [load_, setLoad] = useState<Load>(
    cached ? { state: "ready", version: cached } : { state: "loading" },
  );

  useEffect(() => {
    const hit = cache.get(binding.id);
    if (hit) {
      setLoad({ state: "ready", version: hit });
      return;
    }
    let live = true;
    setLoad({ state: "loading" });
    load(binding.name, binding.id)
      .then((version) => {
        cache.set(binding.id, version);
        if (live) setLoad({ state: "ready", version });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setLoad({
          state: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      });
    // Unmounting mid-flight, or switching badges, must not let a late response
    // overwrite what is on screen.
    return () => {
      live = false;
    };
  }, [binding.id, binding.name, load, cache]);

  const version = load_.state === "ready" ? load_.version : null;
  // The guideline body is a second block of the same <sub_agent_orchestration>
  // section, so it belongs to the same source rather than to a sibling entry.
  const sourceChars =
    version === null
      ? null
      : version.body.length + (version.tool_guideline_body?.length ?? 0);

  return (
    <div
      data-testid="prompt-source"
      className="mt-3 grid gap-3 rounded-lg border border-border bg-surface-subtle p-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <strong>{binding.label}</strong>
        {binding.kind && <span className="text-muted-foreground">{binding.kind}</span>}
        {version?.role && <span className="text-muted-foreground">{version.role}</span>}
        {version && !version.is_active && (
          // The ordinary case: a past turn almost always ran on a version that
          // has since been replaced, and saying so is what keeps the reader
          // from checking it against the editor and finding different text.
          <span className="rounded-full border border-border px-1.5 text-xs text-muted-foreground">
            교체됨
          </span>
        )}
        {binding.renderedTokens !== null && (
          <span className="text-xs text-muted-foreground">
            이 턴에서 {binding.renderedTokens.toLocaleString()} tok
            {binding.renderedChars !== null &&
              ` · ${binding.renderedChars.toLocaleString()} chars`}
          </span>
        )}
        {sourceChars !== null && (
          <span className="text-xs text-muted-foreground">
            원문 {sourceChars.toLocaleString()} chars
          </span>
        )}
      </div>

      {/* Both numbers are shown above, and they will not agree. Saying why here
          is the difference between "the template is 27k, the turn was charged
          for 18k, so filtering dropped a third" and a reader concluding the
          trace is wrong. */}
      <p className="rounded-lg border border-border bg-surface-input p-2.5 text-xs text-muted-foreground">
        아래는 <strong>저장된 소스 템플릿</strong>이고 모델이 실제로 받은 텍스트가
        아닙니다. <code>{"{authenticated_email}"}</code> 같은 자리표시자는 치환 전,
        tool_guideline은 그 턴의 도구 allowlist로 걸러지기 전 전문이며,
        interactive_ui 블록은 조립 과정에서 꼬리로 옮겨집니다. 위의 두 크기가 다른
        이유가 그것입니다.
      </p>

      {load_.state === "loading" && (
        <p className="text-sm text-muted-foreground">프롬프트 원문을 불러오는 중…</p>
      )}
      {load_.state === "error" && (
        <p className="text-sm text-destructive">
          이 버전을 불러오지 못했습니다 — {load_.message}
        </p>
      )}
      {version && <Block title="Body">{clip(version.body)}</Block>}
      {version?.tool_guideline_body && (
        <Block title="Tool guideline body">{clip(version.tool_guideline_body)}</Block>
      )}
      {version?.description && (
        <p className="text-xs text-muted-foreground">{version.description}</p>
      )}
    </div>
  );
}
